/**
 * HID 设备通信协议处理模块
 * 包含帧格式定义、校验和计算、命令构造和响应解析
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 */
import { CommandType, type DeviceStatus, type DeviceDetail } from './types'

// 协议帧头标识
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

/**
 * Calculate checksum for protocol frame
 * Algorithm: Sum of header + all data bytes, then 16-bit complement
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * 构建协议命令帧
 * @param command - 命令类型
 * @param data - 命令数据数组
 * @returns 完整的 Uint8Array 帧
 */
export function buildCommand(
  command: CommandType,
  data: number[] = []
): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  const length = data.length + 5 // header(2) + length(1) + command(1) + data(n) + checksum(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * 将十六进制字符串转换为 Uint8Array
 * @param hexString - 格式: "AA 53 08 80 00 01 00 7A"（空格分隔）
 * @returns 转换后的字节数组
 */
export function hexStringToBytes(hexString: string): Uint8Array {
  const hexArray = hexString.trim().split(/\s+/)
  return new Uint8Array(hexArray.map(hex => parseInt(hex, 16)))
}

/**
 * 将字节数组转换为十六进制字符串
 * @param bytes - 字节数组
 * @returns 十六进制字符串（大写字母，空格分隔）
 */
export function bytesToHexString(bytes: Uint8Array | number[]): string {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0').toUpperCase())
    .join(' ')
}

/**
 * 解析设备状态响应
 * 从响应帧中提取设备的各项状态参数
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceStatus 对象或 null（如果解析失败）
 */
export function parseHeadsetResponse(hexString: string): DeviceStatus | null {
  const bytes = hexStringToBytes(hexString)

  // Validate minimum length
  if (bytes.length < 8) {
    console.warn('Response too short:', bytes.length)
    return null
  }

  // Validate header
  if (bytes[0] !== FRAME_HEADER[0] || bytes[1] !== FRAME_HEADER[1]) {
    console.warn('Invalid frame header:', bytes[0], bytes[1])
    return null
  }

  // Validate checksum
  const payload = Array.from(bytes.slice(2, -1))
  const receivedChecksum = bytes[bytes.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)

  if (receivedChecksum !== calculatedChecksum) {
    console.warn('Checksum mismatch:', receivedChecksum, calculatedChecksum)
    return null
  }

  const B = bytes.slice(6, 6 + 56)

  // 对齐旧项目 server-1.js parsePackedVersion：小端 packed version -> Vx.y.z
  const parsePackedVersion = (hi: number, lo: number): string => {
    if ((hi === 0 && lo === 0) || hi == null || lo == null) return ''
    const word = (lo << 8) | hi
    const major = (word >> 12) & 0x0F
    const minor = (word >> 7) & 0x1F
    const patch = word & 0x7F
    return `V${major}.${minor}.${patch}`
  }

  return {
    headsetConnected: B[0] || 0,
    micVolumeLevel: B[1] || 0,
    micMute: B[2] || 0,
    speakerVolumeLevel: B[3] || 0,
    speakerMute: B[4] || 0,
    chickenOneKey: B[6] || 0,
    noiseReductionMode: B[9] || 1,
    noiseReductionEnabled: B[10] || 0,
    ledMode: B[11] || 0,
    eqMode: B[12] || 0,
    linkStatus: B[13] || 0,
    batteryLevel: B[14] || 0,
    powerOffStatus: B[15] || 0,
    headsetColor: B[16] || 0,
    batteryCharging: B[17] || 0,
    bluetoothConnectionMark: B[18] || 0,
    productType: B[19] || 0,
    dongleVersion: parsePackedVersion(B[28] || 0, B[29] || 0),
    softwareVersion: parsePackedVersion(B[30] || 0, B[31] || 0),
    bluetoothVersion: parsePackedVersion(B[32] || 0, B[33] || 0),
    noiseCancelVersion: parsePackedVersion(B[34] || 0, B[35] || 0),
    audioChannelMode: B[36] || 0,
    mixVolume: B[37] || 0,
  }
}

/**
 * 解析设备详情响应
 * 从响应帧中提取设备名称、版本等信息
 * @param hexString - 十六进制响应帧字符串
 * @returns DeviceDetail 对象或 null（如果解析失败）
 */
export function parseDeviceDetailResponse(hexString: string): DeviceDetail | null {
  const bytes = hexStringToBytes(hexString)
  if (bytes.length < 20) return null
  if (bytes[0] !== FRAME_HEADER[0] || bytes[1] !== FRAME_HEADER[1]) return null

  const totalLength = bytes[2]
  const cmd = bytes[3]
  if (cmd !== 0x82) return null

  const payload = Array.from(bytes.slice(2, -1))
  const receivedChecksum = bytes[bytes.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)
  if (receivedChecksum !== calculatedChecksum) return null

  // 对齐旧项目：0x82 数据区从第 6 字节开始，长度 54 字节
  const B = bytes.slice(6, 6 + 54)
  if (B.length < 28 || totalLength < 8) return null

  const brandLetters = String.fromCharCode(B[0], B[1], B[2]).replace(/\0/g, '-')
  const brandNumber = String(B[3] || 0).padStart(3, '0')
  const brandCode = `${brandLetters}${brandNumber}`

  const categoryLetters = String.fromCharCode(B[4], B[5], B[6]).replace(/\0/g, '-')
  const categoryNumber = String(B[7] || 0).padStart(3, '0')
  const categoryStr = `${categoryLetters}${categoryNumber}`

  let deviceCategory: DeviceDetail['deviceCategory'] = 'unknown'
  if (categoryLetters.startsWith('MIC')) deviceCategory = 'microPhone'
  else if (categoryLetters.startsWith('HEA') || categoryLetters.startsWith('HPH')) deviceCategory = 'headPhone'

  const modelLetters = String.fromCharCode(B[8], B[9]).replace(/\0/g, '-')
  const modelNumbers = String.fromCharCode(B[10], B[11]).replace(/\0/g, '0')
  const modelCode = `${modelLetters}${modelNumbers}`

  const configByte = B[12] || 0
  const colorValue = B[13] || 0
  const pid = ((B[14] || 0) << 8) | (B[15] || 0)
  const vid = ((B[16] || 0) << 8) | (B[17] || 0)

  const parsePackedVersion = (hi: number, lo: number): string => {
    if ((hi === 0 && lo === 0) || hi == null || lo == null) return ''
    const word = (lo << 8) | hi
    const major = (word >> 12) & 0x0F
    const minor = (word >> 7) & 0x1F
    const patch = word & 0x7F
    return `V${major}.${minor}.${patch}`
  }

  return {
    deviceCategory,
    vendorId: vid,
    productId: pid,
    deviceName: modelCode || categoryStr || brandCode,
    deviceSerial: Array.from(B.slice(20, 28)).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(''),
    firmwareVersion: '',
    hardwareVersion: parsePackedVersion(B[18] || 0, B[19] || 0),
    protocolVersion: '',
    deviceTypeCode: bytesToHexString(B.slice(4, 12)).replace(/ /g, ''),
    softwareVersion: '',
    bluetoothVersion: '',
    noiseCancelVersion: '',
    dongleVersion: '',
  }
}

/**
 * Build EQ command data
 */
export function buildEqCommand(eqValues: number[]): Uint8Array {
  // Map EQ gain values (-12 to +12) to device protocol values
  const mappedValues = eqValues.map(gain => {
    // Convert from dB to device-specific value
    return Math.round((gain + 12) * 2) // Example mapping
  })

  return buildCommand(CommandType.SET_EQ, mappedValues)
}

/**
 * Build mute command
 */
export function buildMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * Build mic mute command
 */
export function buildMicMuteCommand(muted: boolean): Uint8Array {
  return buildCommand(CommandType.SET_MIC_MUTE, [muted ? 0x01 : 0x00])
}

/**
 * Build get status command
 * Match original project: AA 53 08 80 00 01 00 7A
 */
export function buildGetStatusCommand(): Uint8Array {
  // Hardcoded to match original project exactly
  return hexStringToBytes('AA 53 08 80 00 01 00 7A')
}

/**
 * Build get device detail command
 * Match original project: AA 53 08 82 00 01 00 78
 */
export function buildGetDeviceDetailCommand(): Uint8Array {
  // Hardcoded to match original project exactly
  return hexStringToBytes('AA 53 08 82 00 01 00 78')
}

// Command hex strings (for direct use if needed)
export const COMMANDS = {
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const
